import { useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { GitPullRequestArrow, Search, Trash2, UserPlus, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  applyPublicationReconcileDecision,
  getKillSwitchState,
  getPublicationReconcileDiff,
  getWorkItem,
  listArtifacts,
  listArtifactWorkItemLinks,
  listCodebases,
} from '@/lib/api';
import type {
  Artifact,
  ArtifactWorkItemLink,
  CodebaseResponse,
  GitHubWorkItem,
  GitHubWorkItemDetailResponse,
  ReconcileDiff,
} from '@/lib/api';

type Mode = 'picker' | 'manual';

interface Props {
  initialMode?: Mode;
  initialArtifactId?: string;
  initialWorkItemId?: string;
}

const KIND_LABEL: Record<ReconcileDiff['rows'][number]['kind'], string> = {
  tracked: 'tracked',
  untracked: 'untracked',
  row_pointing_at_missing_comment: 'row pointing at missing comment',
};

function canQuery(artifactId: string, workItemId: string): boolean {
  return artifactId.trim().length > 0 && workItemId.trim().length > 0;
}

function sortByName<T extends { name: string }>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => a.name.localeCompare(b.name));
}

function sortArtifacts(items: readonly Artifact[]): Artifact[] {
  return [...items].sort((a, b) => a.title.localeCompare(b.title));
}

function workItemLabel(item: GitHubWorkItem): string {
  return `${item.githubRepoOwner}/${item.githubRepoName}#${String(item.githubIssueNumber)} — ${item.title}`;
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

export function AdminPublicationReconcilePanel({
  initialMode = 'picker',
  initialArtifactId = '',
  initialWorkItemId = '',
}: Props): React.ReactElement {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [codebaseId, setCodebaseId] = useState('');
  const [artifactId, setArtifactId] = useState(initialMode === 'manual' ? initialArtifactId : '');
  const [workItemId, setWorkItemId] = useState(initialMode === 'manual' ? initialWorkItemId : '');
  const [submitted, setSubmitted] = useState(() =>
    initialMode === 'manual' ? canQuery(initialArtifactId, initialWorkItemId) : false
  );
  const [message, setMessage] = useState<string | null>(null);

  const codebasesQuery = useQuery<CodebaseResponse[]>({
    queryKey: ['admin-reconcile-codebases'],
    queryFn: listCodebases,
    staleTime: 60_000,
    enabled: mode === 'picker',
  });
  // Server caps `limit` at 200. When the codebase has more artifacts than that
  // we surface a truncation notice and route admins to manual mode.
  const ARTIFACT_PAGE_SIZE = 200;
  const artifactsQuery = useQuery({
    queryKey: ['admin-reconcile-artifacts', codebaseId],
    queryFn: () => listArtifacts(codebaseId, { includeArchived: true, limit: ARTIFACT_PAGE_SIZE }),
    enabled: mode === 'picker' && codebaseId.length > 0,
    staleTime: 30_000,
  });
  const linksQuery = useQuery<ArtifactWorkItemLink[]>({
    queryKey: ['admin-reconcile-links', artifactId],
    queryFn: () => listArtifactWorkItemLinks(artifactId.trim()),
    enabled: mode === 'picker' && artifactId.trim().length > 0,
    staleTime: 30_000,
  });

  const links = linksQuery.data ?? [];
  // Dedupe before issuing useQueries so we don't fire identical requests for
  // multi-linked work items.
  const uniqueLinks = useMemo(() => {
    const seen = new Set<string>();
    const out: ArtifactWorkItemLink[] = [];
    for (const link of links) {
      if (seen.has(link.workItemId)) continue;
      seen.add(link.workItemId);
      out.push(link);
    }
    return out;
  }, [links]);

  const workItemQueries = useQueries({
    queries: uniqueLinks.map(link => ({
      queryKey: ['github-work-item', link.workItemId] as const,
      queryFn: (): Promise<GitHubWorkItemDetailResponse> => getWorkItem(link.workItemId),
      staleTime: 60_000,
    })),
  });

  const codebases = useMemo(() => sortByName(codebasesQuery.data ?? []), [codebasesQuery.data]);
  const artifacts = useMemo(
    () => sortArtifacts(artifactsQuery.data?.artifacts ?? []),
    [artifactsQuery.data]
  );

  const workItemOptions = useMemo(() => {
    const opts = uniqueLinks.map((link, idx) => {
      const detail = workItemQueries[idx]?.data?.workItem;
      return {
        id: link.workItemId,
        label: detail ? workItemLabel(detail) : link.workItemId,
      };
    });
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [uniqueLinks, workItemQueries]);

  const queryEnabled = submitted && canQuery(artifactId, workItemId);
  const diffQuery = useQuery({
    queryKey: ['adminPublicationReconcile', artifactId, workItemId],
    queryFn: () => getPublicationReconcileDiff(artifactId.trim(), workItemId.trim()),
    enabled: queryEnabled,
  });
  const killSwitchQuery = useQuery({
    queryKey: ['adminKillSwitchStatus'],
    queryFn: getKillSwitchState,
  });

  const publicationDisabled = killSwitchQuery.data?.publication === true;
  const decisionMutation = useMutation({
    mutationFn: (input: { action: 'adopt' | 'delete'; externalCommentId: string }) =>
      applyPublicationReconcileDecision({
        artifactId: artifactId.trim(),
        workItemId: workItemId.trim(),
        action: input.action,
        externalCommentId: input.externalCommentId,
      }),
    onSuccess: async result => {
      setMessage(
        result.action === 'adopt' ? 'Comment adopted.' : `Delete outcome: ${result.outcome}`
      );
      await queryClient.invalidateQueries({
        queryKey: ['adminPublicationReconcile', artifactId, workItemId],
      });
    },
    onError: error => {
      setMessage(errorMessage(error, 'Publication reconcile action failed'));
    },
  });

  const rows = diffQuery.data?.rows ?? [];

  const codebasePickerDisabled =
    codebasesQuery.isLoading || codebasesQuery.isError || codebases.length === 0;
  const artifactPickerDisabled =
    codebaseId.length === 0 ||
    artifactsQuery.isLoading ||
    artifactsQuery.isError ||
    artifacts.length === 0;
  const workItemPickerDisabled =
    artifactId.trim().length === 0 ||
    linksQuery.isLoading ||
    linksQuery.isError ||
    workItemOptions.length === 0;

  function switchMode(next: Mode): void {
    if (mode === next) return;
    setMode(next);
    setCodebaseId('');
    setArtifactId('');
    setWorkItemId('');
    setSubmitted(false);
    setMessage(null);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GitPullRequestArrow className="size-4" />
          Artifact publications
        </CardTitle>
        <CardDescription>
          Reconcile orphaned managed comments on a GitHub issue. The default picker covers the
          common case; use the UUID escape hatch when an artifact is archived, in a codebase you
          can&rsquo;t see, or otherwise outside the picker&rsquo;s reach.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="inline-flex w-fit rounded-md border border-border p-1">
          <button
            type="button"
            aria-pressed={mode === 'picker'}
            className={`rounded px-3 py-1.5 text-sm font-medium transition-colors ${mode === 'picker' ? 'bg-[oklch(0.72_0.14_225/0.15)] text-accent-bright' : 'text-text-secondary hover:text-text-primary'}`}
            onClick={() => {
              switchMode('picker');
            }}
          >
            By name
          </button>
          <button
            type="button"
            aria-pressed={mode === 'manual'}
            className={`rounded px-3 py-1.5 text-sm font-medium transition-colors ${mode === 'manual' ? 'bg-[oklch(0.72_0.14_225/0.15)] text-accent-bright' : 'text-text-secondary hover:text-text-primary'}`}
            onClick={() => {
              switchMode('manual');
            }}
          >
            By UUID
          </button>
        </div>

        {mode === 'picker' ? (
          <div className="grid gap-3 md:grid-cols-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-muted-foreground">Codebase</span>
              <select
                className="flex h-9 w-full rounded-md border border-border bg-surface px-3 text-sm shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                value={codebaseId}
                disabled={codebasePickerDisabled}
                aria-label="Select codebase"
                onChange={event => {
                  setCodebaseId(event.target.value);
                  setArtifactId('');
                  setWorkItemId('');
                  setSubmitted(false);
                }}
              >
                <option value="">
                  {codebasesQuery.isLoading
                    ? 'Loading…'
                    : codebasesQuery.isError
                      ? 'Failed to load codebases'
                      : codebases.length === 0
                        ? 'No codebases registered'
                        : 'Select a codebase…'}
                </option>
                {codebases.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {codebasesQuery.isError && (
                <span className="text-xs text-destructive">
                  {errorMessage(codebasesQuery.error, 'Failed to load codebases')}
                </span>
              )}
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-muted-foreground">Artifact</span>
              <select
                className="flex h-9 w-full rounded-md border border-border bg-surface px-3 text-sm shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                value={artifactId}
                disabled={artifactPickerDisabled}
                aria-label="Select artifact"
                onChange={event => {
                  setArtifactId(event.target.value);
                  setWorkItemId('');
                  setSubmitted(false);
                }}
              >
                <option value="">
                  {codebaseId.length === 0
                    ? 'Select a codebase first'
                    : artifactsQuery.isLoading
                      ? 'Loading…'
                      : artifactsQuery.isError
                        ? 'Failed to load artifacts'
                        : artifacts.length === 0
                          ? 'No artifacts in this codebase'
                          : 'Select an artifact…'}
                </option>
                {artifacts.map(a => (
                  <option key={a.id} value={a.id}>
                    {a.status === 'approved' ? a.title : `${a.title} [${a.status}]`}
                  </option>
                ))}
              </select>
              {artifactsQuery.isError && (
                <span className="text-xs text-destructive">
                  {errorMessage(artifactsQuery.error, 'Failed to load artifacts')}
                </span>
              )}
              {artifacts.length >= ARTIFACT_PAGE_SIZE && (
                <span className="text-xs text-muted-foreground">
                  Showing the first {String(ARTIFACT_PAGE_SIZE)} artifacts. If yours is missing,
                  switch to By UUID.
                </span>
              )}
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-muted-foreground">Work item</span>
              <select
                className="flex h-9 w-full rounded-md border border-border bg-surface px-3 text-sm shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                value={workItemId}
                disabled={workItemPickerDisabled}
                aria-label="Select work item"
                onChange={event => {
                  setWorkItemId(event.target.value);
                  setSubmitted(false);
                }}
              >
                <option value="">
                  {artifactId.trim().length === 0
                    ? 'Select an artifact first'
                    : linksQuery.isLoading
                      ? 'Loading…'
                      : linksQuery.isError
                        ? 'Failed to load linked work items'
                        : workItemOptions.length === 0
                          ? 'No linked work items'
                          : 'Select a work item…'}
                </option>
                {workItemOptions.map(opt => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {linksQuery.isError && (
                <span className="text-xs text-destructive">
                  {errorMessage(linksQuery.error, 'Failed to load linked work items')}
                </span>
              )}
            </label>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-muted-foreground">Artifact ID</span>
              <input
                className="h-9 rounded-md border border-border bg-background px-3 text-sm"
                value={artifactId}
                aria-label="Artifact UUID"
                onChange={event => {
                  setArtifactId(event.currentTarget.value);
                  setSubmitted(false);
                }}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-muted-foreground">Work item ID</span>
              <input
                className="h-9 rounded-md border border-border bg-background px-3 text-sm"
                value={workItemId}
                aria-label="Work item UUID"
                onChange={event => {
                  setWorkItemId(event.currentTarget.value);
                  setSubmitted(false);
                }}
              />
            </label>
          </div>
        )}

        <div>
          <Button
            type="button"
            disabled={!canQuery(artifactId, workItemId)}
            onClick={() => {
              setSubmitted(true);
              setMessage(null);
            }}
          >
            <Search className="mr-2 size-4" />
            Load
          </Button>
        </div>

        {publicationDisabled && (
          <div className="text-sm text-muted-foreground">
            Publication actions are disabled by the current kill-switch state.
          </div>
        )}
        {message && <div className="text-sm text-muted-foreground">{message}</div>}
        {diffQuery.isError && (
          <div className="text-sm text-destructive">
            {errorMessage(diffQuery.error, 'Failed to load diff')}
          </div>
        )}
        {diffQuery.isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}

        {rows.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-muted/40 text-left text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Comment</th>
                  <th className="px-4 py-3 font-medium">Kind</th>
                  <th className="px-4 py-3 font-medium">Preview</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => {
                  const actionDisabled = publicationDisabled || decisionMutation.isPending;
                  return (
                    <tr
                      key={`${row.kind}:${row.externalCommentId}:${row.publicationId ?? ''}`}
                      className="border-t border-border"
                    >
                      <td className="px-4 py-3 align-top font-mono text-xs">
                        {row.externalCommentId}
                        {row.publicationDriftState && (
                          <div className="mt-1 text-muted-foreground">
                            {row.publicationDriftState}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <Badge variant={row.kind === 'untracked' ? 'secondary' : 'outline'}>
                          {KIND_LABEL[row.kind]}
                        </Badge>
                      </td>
                      <td className="max-w-md px-4 py-3 align-top text-muted-foreground">
                        <div className="line-clamp-3 whitespace-pre-wrap break-words">
                          {row.bodyPreview}
                        </div>
                        <div className="mt-1 text-xs">{row.bodyPreviewSource}</div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={actionDisabled || row.kind !== 'untracked'}
                            onClick={() => {
                              decisionMutation.mutate({
                                action: 'adopt',
                                externalCommentId: row.externalCommentId,
                              });
                            }}
                          >
                            <UserPlus className="mr-2 size-4" />
                            Adopt
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={
                              actionDisabled || row.kind === 'row_pointing_at_missing_comment'
                            }
                            onClick={() => {
                              decisionMutation.mutate({
                                action: 'delete',
                                externalCommentId: row.externalCommentId,
                              });
                            }}
                          >
                            <Trash2 className="mr-2 size-4" />
                            Delete
                          </Button>
                          <Button type="button" size="sm" variant="ghost">
                            <X className="mr-2 size-4" />
                            Skip
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
